import { APIURL, deleteData, getData, postData } from "../fetch";

/**
 * The backend stores primary keys as `long_id` and exposes them to this app as `id`.
 *
 * @typedef {{
 *  title: string,
 *  description: string|null,
 *  location: string,
 *  start_date: string,
 *  start_time: string|null,
 *  end_date: string|null,
 *  end_time: string|null,
 *  status: "draft"|"published"|"completed"|"cancelled",
 *  registrationOpen: boolean,
 *  feedbackOpen: boolean,
 *  client_logo_file_id: string|null
 * }} EventInput
 */

/**
 * @typedef {EventInput & {
 *  id: string,
 *  short_code: string,
 *  feedbackStarted: boolean,
 *  date_added: string,
 *  date_updated: string,
 *  added_by: string,
 *  updated_by: string,
 *  registrations?: EventRegistrationObject[],
 *  feedback_questions?: EventFeedbackQuestionObject[],
 *  feedback_submissions?: EventFeedbackSubmissionObject[]
 * }} EventObject
 */

/**
 * @typedef {{
 *  full_name: string,
 *  email: string|null,
 *  phone: string|null,
 *  organisation: string|null,
 *  job_title: string|null
 * }} EventRegistrationInput
 */

/**
 * @typedef {EventRegistrationInput & {
 *  id: string,
 *  event_id: string,
 *  registration_method: "self"|"desk",
 *  checkedIn: boolean,
 *  check_in_time: string|null,
 *  checked_in_by: string|null,
 *  date_added: string,
 *  date_updated: string,
 *  added_by: string|null,
 *  updated_by: string|null
 * }} EventRegistrationObject
 */

/**
 * @typedef {{
 *  question: string,
 *  question_type: "rating"|"textarea"|"options",
 *  allow_other?: 0|1,
 *  options?: {id?: string, label: string, sort_order?: number}[]
 * }} EventFeedbackQuestionInput
 */

/**
 * @typedef {EventFeedbackQuestionInput & {
 *  id: string,
 *  event_id: string,
 *  date_added: string,
 *  date_updated: string,
 *  added_by: string,
 *  updated_by: string
 * }} EventFeedbackQuestionObject
 */

/**
 * @typedef {{
 *  id: string,
 *  submission_id: string,
 *  question_id: string,
 *  option_id: string|null,
 *  response: string,
 *  date_added: string
 * }} EventFeedbackResponseObject
 */

/**
 * @typedef {{
 *  id: string,
 *  event_id: string,
 *  date_added: string,
 *  responses: EventFeedbackResponseObject[]
 * }} EventFeedbackSubmissionObject
 */

/** Create an event. `short_code` and `id` are generated by the backend. */
const createEvent = data => postData({ url: `${APIURL}/events`, data });

/** Update an event by its API `id` (database `long_id`). */
const updateEvent = (data, id) => postData({ url: `${APIURL}/events/${id}`, data });

/** Get all events. */
const getEvents = () => getData({ url: `${APIURL}/events` });

/** Get one event and optionally include its related schema objects. */
const getEvent = (id, includes = []) => getData({
    url: `${APIURL}/events/${id}`,
    ...(includes.length ? { data: { include: includes.join(",") } } : {})
});

/** Delete an event and its related registrations and feedback records. */
const deleteEvent = id => deleteData({ url: `${APIURL}/events/${id}` });

/** Load the public event landing page details using its short code. */
const getPublicEvent = shortCode => getData({
    url: `${APIURL}/events/public/${encodeURIComponent(shortCode)}`
});

/** Get all registrations for one event. */
const getRegistrations = eventId => getData({ url: `${APIURL}/event-registrations/${eventId}` });

/** Add a desk registration to an event. */
const createRegistration = (eventId, data) => postData({ url: `${APIURL}/event-registrations/${eventId}`, data });

/** Delete a registration belonging to an event. */
const deleteRegistration = (eventId, registrationId) => deleteData({
    url: `${APIURL}/event-registrations/${eventId}/${registrationId}`
});

/** Get configured questions and anonymous submissions for one event. */
const getEventFeedback = eventId => getData({ url: `${APIURL}/event-feedback/${eventId}` });

/** Add a feedback question to an event. */
const createFeedbackQuestion = (eventId, data) => postData({
    url: `${APIURL}/event-feedback/${eventId}/questions`, data
});

/** Update a feedback question before feedback has started. */
const updateFeedbackQuestion = (eventId, questionId, data) => postData({
    url: `${APIURL}/event-feedback/${eventId}/questions/${questionId}`, data
});

/** Delete a feedback question before feedback has started. */
const deleteFeedbackQuestion = (eventId, questionId) => deleteData({
    url: `${APIURL}/event-feedback/${eventId}/questions/${questionId}`
});

/** Load public event registration details using its short code. */
const getPublicRegistration = shortCode => getData({
    url: `${APIURL}/event-registrations/public/${encodeURIComponent(shortCode)}`
});

/** Submit a self-registration using an event short code. */
const submitPublicRegistration = (shortCode, data) => postData({
    url: `${APIURL}/event-registrations/public/${encodeURIComponent(shortCode)}`, data
});

/** Load the public anonymous feedback form using an event short code. */
const getPublicFeedback = shortCode => getData({
    url: `${APIURL}/event-feedback/public/${encodeURIComponent(shortCode)}`
});

/**
 * Submit one anonymous feedback form.
 * @param {string} shortCode
 * @param {{question_id: string, option_id?: string|null, response: string}[]} responses
 */
const submitPublicFeedback = (shortCode, responses) => postData({
    url: `${APIURL}/event-feedback/public/${encodeURIComponent(shortCode)}`,
    data: { responses }
});

export {
    createEvent,
    updateEvent,
    getEvents,
    getEvent,
    deleteEvent,
    getPublicEvent,
    getRegistrations,
    createRegistration,
    deleteRegistration,
    getEventFeedback,
    createFeedbackQuestion,
    updateFeedbackQuestion,
    deleteFeedbackQuestion,
    getPublicRegistration,
    submitPublicRegistration,
    getPublicFeedback,
    submitPublicFeedback
};
